import React from 'react';

import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { archetypeEnumLabel } from '../../../data/deckArchetypeProfileMarkers';

/**
 * Archetype profile panel — modeled on `CombosProfilePanel`. Bundles the
 * marker chip groups (drivers / hybrids / pre-game disclosures / tuning)
 * with a facet grid that surfaces concentration, per-major roll-up, the
 * top sub-archetype "markers", and the kindred summary.
 *
 * The panel reads `deck.details.archetypes.profile` only — the parent
 * pane decides when to render the collapsible shell. Returns `null` when
 * the profile is missing so the pane can skip the whole section on
 * cached decks that pre-date the cache bump.
 */

// ---- Tiny presentational atoms ---------------------------------------

function FacetHeader({ title, density, tier, headline }) {
    return (
        <div className="powerFacetHeader">
            <div className="powerFacetTitleRow">
                <span className="powerFacetTitle">{title}</span>
                {density && density !== 'none' && (
                    <span className={`archetypePill archetypePill--density-${density}`}>
                        {archetypeEnumLabel('density', density)}
                    </span>
                )}
                {tier && tier !== 'trace' && (
                    <span className={`archetypePill archetypePill--tier-${tier}`}>
                        {archetypeEnumLabel('tier', tier)}
                    </span>
                )}
            </div>
            {headline && <span className="powerFacetHeadline">{headline}</span>}
        </div>
    );
}

function StatRow({ stats }) {
    const visible = (stats || []).filter((s) => s && s.value != null && s.value !== '');
    if (visible.length === 0) return null;
    return (
        <div className="powerFacetStats">
            {visible.map((s, i) => (
                <span key={i} className="powerFacetStat" title={s.hint || undefined}>
                    <strong>{s.value}</strong> {s.label}
                </span>
            ))}
        </div>
    );
}

function BigNumber({ value, label }) {
    if (value == null) return null;
    return (
        <div className="powerFacetBigNumber">
            <span className="powerFacetBigValue">{value}</span>
            <span className="powerFacetBigLabel">{label}</span>
        </div>
    );
}

// ---- Markers section --------------------------------------------------

function MarkerGroup({ label, tone, chips }) {
    if (!chips || chips.length === 0) return null;
    return (
        <div className={`combosMarkerGroup combosMarkerGroup--${tone}`}>
            <span className="combosMarkerGroupLabel">{label}</span>
            <div className="combosMarkerChipRow">
                {chips.map((chip) => (
                    <span
                        key={chip.key}
                        className={`powerPatternChip powerPatternChip--${chip.tone}`}
                        title={chip.detail || undefined}
                    >
                        {chip.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function MarkersSection({ headline, drivers, hybridSignals, disclosures, tuning }) {
    const hasAny =
        (drivers && drivers.length > 0) ||
        (hybridSignals && hybridSignals.length > 0) ||
        (disclosures && disclosures.length > 0) ||
        (tuning && tuning.length > 0);
    if (!headline && !hasAny) return null;
    return (
        <div className="combosProfileMarkers">
            {headline && <p className="combosHeadline">{headline}</p>}
            {hasAny && (
                <div className="combosMarkerGroups">
                    <MarkerGroup label="Why" tone="alert" chips={drivers} />
                    <MarkerGroup label="Hybrid" tone="cool" chips={hybridSignals} />
                    <MarkerGroup label="Pre-game" tone="warn" chips={disclosures} />
                    <MarkerGroup label="Tuning" tone="cool" chips={tuning} />
                </div>
            )}
        </div>
    );
}

// ---- Facets ---------------------------------------------------------

function ConcentrationFacet({ concentration, activeMajorCount, activeSubCount }) {
    if (!concentration || concentration.kind === 'empty') return null;
    const kindLabel = archetypeEnumLabel('concentration', concentration.kind);
    const top = concentration.topShare != null ? Math.round(concentration.topShare * 100) : null;
    const topTwo = concentration.topTwoShare != null ? Math.round(concentration.topTwoShare * 100) : null;
    const diversity = concentration.diversityIndex != null ? concentration.diversityIndex.toFixed(2) : null;

    const stats = [
        topTwo != null ? { value: `${topTwo}%`, label: 'top 2 majors' } : null,
        activeMajorCount ? { value: activeMajorCount, label: 'active majors', hint: 'Majors with share ≥ 5%' } : null,
        activeSubCount
            ? {
                  value: activeSubCount,
                  label: 'active subs',
                  hint: 'Sub-archetypes with share ≥ 5% of total',
              }
            : null,
        diversity != null
            ? {
                  value: diversity,
                  label: 'diversity',
                  hint: 'Simpson index — higher = more smeared identity',
              }
            : null,
    ];

    return (
        <div className="powerFacet">
            <FacetHeader title="Concentration" headline={kindLabel} />
            <BigNumber value={top != null ? `${top}%` : null} label="primary share" />
            <StatRow stats={stats} />
        </div>
    );
}

function MajorShareBar({ entry, maxShare }) {
    const share = entry.share || 0;
    const fill = maxShare > 0 ? Math.min(100, (share / maxShare) * 100) : 0;
    const densityLabel = archetypeEnumLabel('density', entry.density);
    return (
        <div className="archetypeMajorRow">
            <div className="archetypeMajorRowHead">
                <span className="archetypeMajorRowLabel">{getPrettyLabel(entry.major) || entry.major}</span>
                <span className="archetypeMajorRowValue">{Math.round(share * 100)}%</span>
            </div>
            <div className="archetypeMajorRowBarTrack">
                <div
                    className={`archetypeMajorRowBarFill archetypeMajorRowBarFill--${entry.density || 'none'}`}
                    style={{ width: `${fill}%` }}
                />
            </div>
            <div className="archetypeMajorRowMeta">
                {densityLabel && (
                    <span className={`archetypePill archetypePill--density-${entry.density}`}>{densityLabel}</span>
                )}
                {entry.topSub && (
                    <span className="archetypeMajorRowTopSub" title="Top sub-archetype">
                        ↳ {getPrettyLabel(entry.topSub.sub) || entry.topSub.sub}
                    </span>
                )}
                {entry.pieceCount > 0 && (
                    <span className="archetypeMajorRowPieces">
                        {entry.pieceCount} piece{entry.pieceCount === 1 ? '' : 's'}
                    </span>
                )}
            </div>
        </div>
    );
}

function MajorsFacet({ majors }) {
    const visible = (majors || []).filter((m) => (m.share || 0) > 0);
    if (visible.length === 0) return null;
    const maxShare = visible.reduce((m, e) => Math.max(m, e.share || 0), 0);
    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader title="Majors" headline="Share of overall archetype weight by major axis." />
            <div className="archetypeMajorRows">
                {visible.map((entry) => (
                    <MajorShareBar key={entry.major} entry={entry} maxShare={maxShare} />
                ))}
            </div>
        </div>
    );
}

function MarkerListRow({ marker, onSelectCard, cards }) {
    const subLabel = getPrettyLabel(marker.sub) || marker.sub;
    const majorLabel = getPrettyLabel(marker.major) || marker.major;
    const densityLabel = archetypeEnumLabel('density', marker.density);
    const tierLabel = archetypeEnumLabel('tier', marker.tier);
    const topPieces = Array.isArray(marker.topPieces) ? marker.topPieces.slice(0, 3) : [];

    return (
        <div className="archetypeMarkerRow">
            <div className="archetypeMarkerRowHead">
                <span className="archetypeMarkerRowSub">{subLabel}</span>
                <span className="archetypeMarkerRowMajor">{majorLabel}</span>
                <span className="archetypeMarkerRowShare">{Math.round((marker.share || 0) * 100)}%</span>
            </div>
            <div className="archetypeMarkerRowMeta">
                {densityLabel && (
                    <span className={`archetypePill archetypePill--density-${marker.density}`}>{densityLabel}</span>
                )}
                {tierLabel && marker.tier !== 'trace' && (
                    <span className={`archetypePill archetypePill--tier-${marker.tier}`}>{tierLabel}</span>
                )}
                <span className="archetypeMarkerRowPieces">
                    {marker.pieceCount} piece{marker.pieceCount === 1 ? '' : 's'}
                </span>
                {marker.commanderParticipates && (
                    <span className="archetypeMarkerRowCommander" title="Commander participates">
                        ★ Commander
                    </span>
                )}
            </div>
            {topPieces.length > 0 && (
                <div className="archetypeMarkerRowPiecesList">
                    {topPieces.map((piece) => {
                        const card = cards?.[piece?.id];
                        const handleClick = () => {
                            if (card && onSelectCard) onSelectCard(card);
                        };
                        const clickable = !!(card && onSelectCard);
                        return (
                            <span
                                key={piece.id}
                                className={`archetypeMarkerPieceChip${
                                    piece.isCommander ? ' archetypeMarkerPieceChip--commander' : ''
                                }${clickable ? ' archetypeMarkerPieceChip--clickable' : ''}`}
                                onClick={clickable ? handleClick : undefined}
                                role={clickable ? 'button' : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                onKeyDown={
                                    clickable
                                        ? (e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  handleClick();
                                              }
                                          }
                                        : undefined
                                }
                                title={clickable ? `View ${piece.name}` : piece.name}
                            >
                                {piece.name}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function MarkersFacet({ markers, cards, onSelectCard }) {
    const visible = (markers || []).filter((m) => m && m.tier !== 'trace');
    const fallback = (markers || []).slice(0, 5);
    const list = visible.length > 0 ? visible.slice(0, 8) : fallback;
    if (list.length === 0) return null;
    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader
                title="Top markers"
                headline="The sub-archetypes carrying the deck — density, tier, and the cards that fired them."
            />
            <div className="archetypeMarkerRows">
                {list.map((marker) => (
                    <MarkerListRow
                        key={`${marker.major}_${marker.sub}`}
                        marker={marker}
                        cards={cards}
                        onSelectCard={onSelectCard}
                    />
                ))}
            </div>
        </div>
    );
}

function KindredFacet({ kindred }) {
    if (!kindred || !kindred.top) return null;
    const top = kindred.top;
    const typeLabel = getPrettyLabel(top.type) || top.type;
    const headline = kindred.isTribal ? `${typeLabel} typal deck` : `Top type: ${typeLabel}`;

    const secondaries = (kindred.significantTribes || []).filter((t) => t && t.type !== top.type).slice(0, 4);

    const stats = [
        { value: top.typeCount, label: `${typeLabel.toLowerCase()}${top.typeCount === 1 ? '' : 's'}` },
        top.effectCount > 0 ? { value: top.effectCount, label: 'type-care effects' } : null,
    ];

    return (
        <div className="powerFacet">
            <FacetHeader title="Typal" headline={headline} />
            <StatRow stats={stats} />
            {top.hasCommanderSynergy && (
                <span className="archetypeKindredCommanderTag">Commander cares about this type</span>
            )}
            {secondaries.length > 0 && (
                <div className="archetypeKindredSecondaries">
                    <span className="powerFacetSubNote">Other types worth noting:</span>
                    <div className="archetypeKindredSecondaryChips">
                        {secondaries.map((t) => (
                            <span key={t.type} className="archetypeKindredSecondaryChip">
                                {getPrettyLabel(t.type) || t.type} <strong>{t.typeCount}</strong>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---- Top-level export ------------------------------------------------

export function ArchetypeProfilePanel({
    profile,
    headline,
    drivers,
    hybridSignals,
    disclosures,
    tuning,
    cards,
    onSelectCard,
}) {
    if (!profile) return null;
    const headlineSection = profile.headline;
    if (!headlineSection || !headlineSection.primary) return null;

    return (
        <div className="combosProfilePanel">
            <MarkersSection
                headline={headline}
                drivers={drivers}
                hybridSignals={hybridSignals}
                disclosures={disclosures}
                tuning={tuning}
            />
            <div className="combosProfileSubsection">
                <header className="combosProfileSubsectionHeader">
                    <span className="combosProfileSubsectionTitle">Mechanical shape</span>
                    <span className="combosProfileSubsectionHint">
                        How focused the identity is, which majors carry it, and the sub-archetypes actually firing.
                    </span>
                </header>
                <div className="powerFacetGrid">
                    <ConcentrationFacet
                        concentration={profile.concentration}
                        activeMajorCount={headlineSection.activeMajorCount}
                        activeSubCount={headlineSection.activeSubCount}
                    />
                    <MajorsFacet majors={profile.majors} />
                    <MarkersFacet markers={profile.markers} cards={cards} onSelectCard={onSelectCard} />
                    <KindredFacet kindred={profile.kindred} />
                </div>
            </div>
        </div>
    );
}
